/**
 * Which payment link (or embedded buy button) a checkout came from.
 *
 * The `/buy` page records it here just before it hands the buyer to checkout,
 * and checkout sends it with the payment-intent request so it can be stored on
 * the order. It is ADVISORY: the server re-validates the shape, keeps it only
 * when the product it names is really in the cart, and never lets it near
 * pricing, tax or the ledger. A forged value can at worst mislabel where one
 * sale came from.
 *
 * WHERE IT IS KEPT, the same arrangement as the referral code in
 * invite-link.ts. In the page's memory, which carries it through every in-app
 * navigation from /buy to checkout (by way of the product page or the cart
 * when the link sends the buyer there) without storing anything. In the tab's
 * sessionStorage (`checkoutAttribution`) ONLY while the visitor has accepted
 * analytics: remembering which link a buyer came through is not strictly
 * necessary for the purchase they asked for, so under the EU rules on device
 * storage it waits for that consent, and withdrawing it removes the stored
 * copy. The stored copy is what lets the attribution survive a reload of the
 * checkout. The consent check is invite-link-client.ts's own,
 * `readConsent()?.analytics === true`.
 *
 * sessionStorage, not localStorage: attribution is "this tab came through that
 * link", and it should die with the tab rather than label a purchase made days
 * later from a bookmark. The TTL, counted from the click in memory and in
 * storage alike, covers a tab left open overnight.
 *
 * The key is disclosed in the Privacy Policy's storage table (COOKIE_ROWS in
 * src/app/privacy-policy/page.tsx) as optional analytics storage, and
 * public-copy.test.ts fails if that row goes, so rename both or neither.
 *
 * MIRRORED on the server by functions/src/lib/checkout-attribution.ts, which
 * owns the authoritative checks. Keep the shape and the patterns in step.
 */

import { isValidProductId, sanitizeRef, type LinkSource } from './payment-link.ts';
import {
  browserSessionStorage, readStorageItem, removeStorageItems, writeStorageItem,
  type KeyValueStorage,
} from './browser-storage.ts';
import { CONSENT_CHANGED_EVENT, readConsent } from './cookie-consent.ts';

export type CheckoutAttribution = {
  productId: string;
  source: LinkSource;
  ref?: string;
};

export const ATTRIBUTION_STORAGE_KEY = 'checkoutAttribution';
export const ATTRIBUTION_TTL_MS = 24 * 60 * 60 * 1000;

/** How far in the future a stored timestamp may sit (clock skew) before it is refused. */
const FUTURE_SKEW_MS = 60_000;

/** Rebuild a clean attribution from anything, or null. Unknown keys are dropped. */
export function sanitizeCheckoutAttribution(raw: unknown): CheckoutAttribution | null {
  if (!raw || typeof raw !== 'object') return null;
  const value = raw as Record<string, unknown>;
  if (!isValidProductId(value.productId)) return null;
  if (value.source !== 'link' && value.source !== 'button') return null;
  const ref = sanitizeRef(value.ref);
  return { productId: value.productId, source: value.source, ...(ref ? { ref } : {}) };
}

/** What a seller is told about where a sale came from, by the source fulfilment stored on it. */
const SALE_SOURCE_LABELS: Readonly<Record<string, string>> = {
  link: 'Via payment link',
  button: 'Via buy button',
  storefront: 'Via your storefront link',
  product: 'Via your product link',
  search: 'Found through search',
  marketplace: 'Found on supa.place',
};

/**
 * The line a seller sees under a sale, as stored on it by fulfilment ("Via
 * payment link · youtube", "Found on supa.place"), or null when the sale has no
 * known source. Re-checked here like everything read back from a document: a
 * sale written before this existed, or a value that is not one of ours, shows
 * nothing rather than a garbled label.
 */
export function attributionLabel(stored: unknown): string | null {
  if (!stored || typeof stored !== 'object') return null;
  const value = stored as Record<string, unknown>;
  const source = value.source;
  if (typeof source !== 'string' || !Object.prototype.hasOwnProperty.call(SALE_SOURCE_LABELS, source)) return null;
  const via = SALE_SOURCE_LABELS[source];
  const ref = sanitizeRef(value.ref);
  return ref ? `${via} · ${ref}` : via;
}

export type SaleSourceSummary = { ownLinks: number; marketplace: number; search: number; unknown: number };

/**
 * A list of sales counted by where they came from: the seller's own links
 * (payment links, buy buttons, links to their storefront or product pages),
 * the marketplace, search, or not known (anything not one of ours, or a sale
 * from before sources were recorded).
 */
export function saleSourceSummary(attributions: readonly unknown[]): SaleSourceSummary {
  const summary: SaleSourceSummary = { ownLinks: 0, marketplace: 0, search: 0, unknown: 0 };
  for (const stored of attributions) {
    const source = attributionLabel(stored) === null ? null : (stored as { source: string }).source;
    if (source === 'marketplace') summary.marketplace += 1;
    else if (source === 'search') summary.search += 1;
    else if (source === null) summary.unknown += 1;
    else summary.ownLinks += 1;
  }
  return summary;
}

/** The summary as the one line a seller reads above their sales, or null when no source is known. */
export function saleSourceSummaryText(summary: SaleSourceSummary): string | null {
  const parts = [
    summary.ownLinks > 0 ? `From your links: ${summary.ownLinks}` : null,
    summary.marketplace > 0 ? `Found on supa.place: ${summary.marketplace}` : null,
    summary.search > 0 ? `Through search: ${summary.search}` : null,
  ].filter((part): part is string => part !== null);
  return parts.length > 0 ? parts.join(' · ') : null;
}

export interface CheckoutAttributionKeeperDeps {
  /** This tab's sessionStorage in the browser; null where there is none to use. */
  storage: KeyValueStorage | null;
  /** Whether the visitor has accepted analytics right now. */
  consented: () => boolean;
  now: () => number;
}

export interface CheckoutAttributionKeeper {
  /** The `/buy` page is handing the buyer on: this tab came through that link. */
  remember(attribution: CheckoutAttribution): void;
  /**
   * The attribution to send with this checkout, or null.
   *
   * Only returned while the linked product is still in the cart: a buyer who
   * followed a link, removed that product and bought something else did not
   * buy through the link. Not consumed by a read, because checkout re-quotes
   * several times per visit.
   */
  read(cartProductIds: readonly string[]): CheckoutAttribution | null;
  /** The visitor accepted or withdrew analytics. */
  consentChanged(): void;
}

type Kept = { attribution: CheckoutAttribution; at: number };

/**
 * The per-page keeper of the attribution: memory always, the tab's
 * sessionStorage only with analytics consent. See WHERE IT IS KEPT above.
 *
 * One difference from invite-link.ts, and it follows from the storage: a
 * referral code lives in localStorage, which every tab shares, so a tab there
 * never writes over a stored code (another tab's link, at least as new). This
 * lives in sessionStorage, which belongs to this tab alone, so the copy in
 * memory is always this tab's newest and accepting analytics stores it over
 * whatever an earlier page in the tab left.
 */
export function createCheckoutAttributionKeeper(deps: CheckoutAttributionKeeperDeps): CheckoutAttributionKeeper {
  let tab: Kept | null = null;

  const fresh = (at: number, nowMs: number) =>
    nowMs - at <= ATTRIBUTION_TTL_MS && at - nowMs <= FUTURE_SKEW_MS;
  const store = (kept: Kept) =>
    writeStorageItem(deps.storage, ATTRIBUTION_STORAGE_KEY, JSON.stringify({ ...kept.attribution, at: kept.at }));
  const forget = () => removeStorageItems(deps.storage, ATTRIBUTION_STORAGE_KEY);

  /** The stored copy, or null. A stale, future-dated or malformed one is removed as it is found. */
  const readStored = (nowMs: number): Kept | null => {
    const raw = readStorageItem(deps.storage, ATTRIBUTION_STORAGE_KEY);
    if (raw === null) return null;
    try {
      const parsed: unknown = JSON.parse(raw);
      const attribution = sanitizeCheckoutAttribution(parsed);
      const at = Number((parsed as { at?: unknown } | null)?.at);
      if (attribution && Number.isFinite(at) && fresh(at, nowMs)) return { attribution, at };
    } catch {
      /* malformed: removed below */
    }
    forget();
    return null;
  };

  return {
    remember(attribution) {
      const clean = sanitizeCheckoutAttribution(attribution);
      if (!clean) return;
      tab = { attribution: clean, at: deps.now() };
      if (deps.consented()) store(tab);
      // Without consent nothing is stored, and a copy left from before the
      // visitor withdrew it goes too.
      else forget();
    },
    read(cartProductIds) {
      const nowMs = deps.now();
      let current: Kept | null = tab && fresh(tab.at, nowMs) ? tab : null;
      if (!current) {
        if (deps.consented()) current = readStored(nowMs);
        else forget();
      }
      if (!current) return null;
      return cartProductIds.includes(current.attribution.productId) ? current.attribution : null;
    },
    consentChanged() {
      if (!deps.consented()) {
        forget();
        return;
      }
      const nowMs = deps.now();
      if (tab && fresh(tab.at, nowMs)) store(tab);
      // Nothing in memory to store: tidy a stored copy that is no longer usable.
      else readStored(nowMs);
    },
  };
}

let browserKeeper: CheckoutAttributionKeeper | null = null;

/**
 * The keeper for this page, made on first use and following the visitor's
 * analytics choice from then on. Null on the server, where there is no tab to
 * keep anything for and module memory would be shared between visitors.
 */
function pageKeeper(): CheckoutAttributionKeeper | null {
  if (typeof window === 'undefined') return null;
  if (!browserKeeper) {
    const keeper = createCheckoutAttributionKeeper({
      storage: browserSessionStorage(),
      consented: () => readConsent()?.analytics === true,
      now: () => Date.now(),
    });
    browserKeeper = keeper;
    try {
      window.addEventListener(CONSENT_CHANGED_EVENT, () => keeper.consentChanged());
    } catch {
      /* no event target: the next remember or read applies the choice instead */
    }
  }
  return browserKeeper;
}

/** Record where this tab's checkout came from. Best effort: never throws. */
export function rememberCheckoutAttribution(attribution: CheckoutAttribution): void {
  pageKeeper()?.remember(attribution);
}

/** The attribution to send with this checkout, or null. See CheckoutAttributionKeeper.read. */
export function readCheckoutAttribution(cartProductIds: readonly string[]): CheckoutAttribution | null {
  return pageKeeper()?.read(cartProductIds) ?? null;
}

// ---- Seller sources ----------------------------------------------------------
//
// How the buyer reached each seller in this tab, for a sale the payment-link
// label above does not cover: a link to the seller's storefront or product page
// from another site ("storefront", "product", with the seller's `?ref=` tag if
// the link had one), a search engine ("search"), or browsing supa.place
// ("marketplace"). The storefront and product pages decide which
// (seller-source-capture.ts) and record it here; checkout sends what is kept,
// and the server keeps only the sellers in the cart. Owner decision 2026-10-18.
//
// The same arrangement as the link label in every respect that matters: page
// memory always, the tab's sessionStorage (`checkoutSources`) only with
// analytics consent, the same 24 hours, and advisory all the way through (the
// server re-checks it and nothing that prices, taxes or books a sale reads it).
// Kept apart from `checkoutAttribution` because it is a list, one entry per
// seller, where that is a single link. Disclosed in the Privacy Policy's
// storage table like that key, and public-copy.test.ts holds the two together.

/** How a buyer reached a seller. MIRRORED as SELLER_SOURCES in functions/src/lib/checkout-attribution.ts. */
export const SELLER_SOURCES = ['storefront', 'product', 'search', 'marketplace'] as const;
export type SellerSource = (typeof SELLER_SOURCES)[number];

export type SellerSourceEntry = { sellerId: string; source: SellerSource; ref?: string };

export const SELLER_SOURCES_STORAGE_KEY = 'checkoutSources';

/** A seller's account id as a page may name it. MIRRORED as ATTRIBUTION_SELLER_ID_PATTERN on the server. */
export const SELLER_ID_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;

/** Sellers remembered per tab: the newest ten. */
const MAX_SELLER_SOURCES = 10;

/**
 * Which source a later one may replace. The seller's own link is the strongest
 * evidence of who brought the buyer, then a search engine, then browsing: a
 * buyer who came through the seller's Instagram and then looked round the
 * marketplace still came through the seller. Between equals the newer wins.
 */
const SOURCE_STRENGTH: Readonly<Record<SellerSource, number>> = {
  storefront: 3,
  product: 3,
  search: 2,
  marketplace: 1,
};

/** Rebuild a clean seller source from anything, or null. Unknown keys are dropped. */
export function sanitizeSellerSourceEntry(raw: unknown): SellerSourceEntry | null {
  if (!raw || typeof raw !== 'object') return null;
  const value = raw as Record<string, unknown>;
  if (typeof value.sellerId !== 'string' || !SELLER_ID_PATTERN.test(value.sellerId)) return null;
  const source = SELLER_SOURCES.find((s) => s === value.source);
  if (!source) return null;
  const ref = sanitizeRef(value.ref);
  return { sellerId: value.sellerId, source, ...(ref ? { ref } : {}) };
}

export interface SellerSourceKeeper {
  /** This page found how the buyer reached a seller. A weaker source never replaces a stronger one. */
  remember(entry: SellerSourceEntry): void;
  /** The sources still in date, newest first. Not consumed: checkout re-quotes. */
  read(): SellerSourceEntry[];
  /** The visitor accepted or withdrew analytics. */
  consentChanged(): void;
}

type KeptSource = { entry: SellerSourceEntry; at: number };

/** The per-page keeper of the seller sources: memory always, sessionStorage only with consent. */
export function createSellerSourceKeeper(deps: CheckoutAttributionKeeperDeps): SellerSourceKeeper {
  // Null until first used, so a page that never touches it never reads storage.
  let tab: KeptSource[] | null = null;

  const fresh = (at: number, nowMs: number) =>
    nowMs - at <= ATTRIBUTION_TTL_MS && at - nowMs <= FUTURE_SKEW_MS;
  const newestFirst = (list: KeptSource[]) => [...list].sort((a, b) => b.at - a.at);
  const forget = () => removeStorageItems(deps.storage, SELLER_SOURCES_STORAGE_KEY);
  const store = (list: KeptSource[]) => writeStorageItem(
    deps.storage,
    SELLER_SOURCES_STORAGE_KEY,
    JSON.stringify({ entries: list.map((kept) => ({ ...kept.entry, at: kept.at })) }),
  );

  /** The stored entries still usable. A copy with nothing usable in it is removed. */
  const readStored = (nowMs: number): KeptSource[] => {
    const raw = readStorageItem(deps.storage, SELLER_SOURCES_STORAGE_KEY);
    if (raw === null) return [];
    const kept: KeptSource[] = [];
    try {
      const entries = (JSON.parse(raw) as { entries?: unknown } | null)?.entries;
      if (Array.isArray(entries)) {
        for (const item of entries) {
          const entry = sanitizeSellerSourceEntry(item);
          const at = Number((item as { at?: unknown } | null)?.at);
          if (entry && Number.isFinite(at) && fresh(at, nowMs)) kept.push({ entry, at });
        }
      }
    } catch {
      /* malformed: removed below */
    }
    if (kept.length === 0) forget();
    return kept;
  };

  /** This tab's sources in date, loading the stored copy on first use when there is consent. */
  const current = (nowMs: number): KeptSource[] => {
    if (tab === null) {
      if (deps.consented()) tab = readStored(nowMs);
      else {
        forget();
        tab = [];
      }
    }
    tab = newestFirst(tab.filter((kept) => fresh(kept.at, nowMs)));
    return tab;
  };

  return {
    remember(entry) {
      const clean = sanitizeSellerSourceEntry(entry);
      if (!clean) return;
      const nowMs = deps.now();
      const list = current(nowMs);
      const existing = list.find((kept) => kept.entry.sellerId === clean.sellerId);
      if (!existing || SOURCE_STRENGTH[clean.source] >= SOURCE_STRENGTH[existing.entry.source]) {
        tab = newestFirst([
          { entry: clean, at: nowMs },
          ...list.filter((kept) => kept.entry.sellerId !== clean.sellerId),
        ]).slice(0, MAX_SELLER_SOURCES);
      }
      if (deps.consented()) store(tab ?? []);
      // Without consent nothing is stored, and a copy left from before the
      // visitor withdrew it goes too.
      else forget();
    },
    read() {
      return current(deps.now()).map((kept) => kept.entry);
    },
    consentChanged() {
      if (!deps.consented()) {
        forget();
        return;
      }
      const nowMs = deps.now();
      const list = tab === null ? [] : newestFirst(tab.filter((kept) => fresh(kept.at, nowMs)));
      if (list.length > 0) store(list);
      // Nothing in memory to store: tidy a stored copy that is no longer usable.
      else readStored(nowMs);
    },
  };
}

let browserSourceKeeper: SellerSourceKeeper | null = null;

/** The seller-source keeper for this page. Null on the server, for pageKeeper's reason. */
function pageSourceKeeper(): SellerSourceKeeper | null {
  if (typeof window === 'undefined') return null;
  if (!browserSourceKeeper) {
    const keeper = createSellerSourceKeeper({
      storage: browserSessionStorage(),
      consented: () => readConsent()?.analytics === true,
      now: () => Date.now(),
    });
    browserSourceKeeper = keeper;
    try {
      window.addEventListener(CONSENT_CHANGED_EVENT, () => keeper.consentChanged());
    } catch {
      /* no event target: the next remember or read applies the choice instead */
    }
  }
  return browserSourceKeeper;
}

/** Record how this tab reached a seller. Best effort: never throws. */
export function rememberSellerSource(entry: SellerSourceEntry): void {
  pageSourceKeeper()?.remember(entry);
}

/**
 * Every seller source this tab holds, newest first, for the checkout request.
 * All of them, not only the cart's sellers: there are at most ten, and the
 * server keeps only the sellers its own copy of the cart contains.
 */
export function readSellerSources(): SellerSourceEntry[] {
  return pageSourceKeeper()?.read() ?? [];
}
